import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { GROUP_ACTIONS } from "components/Actions/types";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Tag, { TAG_COLORS, TAG_SIZES } from "components/DesignComponents/Tag";
import AlertContext from "context/alert/alertContext";
import TableContext from "context/table/tableContext";
import {
    maxDialogWidth,
    TRI_SOURCES,
    useApi,
    useFormattedNumber,
    useMediaQueries,
    useWindowLocation
} from "shared";

import DialogWithConfirmation from "../DialogWithConfirmation";
import { DeleteSelectedItemsProps } from "./types/interfaces";

const DeleteSelectedItems = ({
    isOpen,
    action,
    close
}: DeleteSelectedItemsProps) => {
    const { setBackgroundActionId } = useContext(AlertContext);
    const { selectedRows, deselectAllRows } = useContext(TableContext);

    const { t } = useTranslation();
    const { bulkDeleteData, deleteData, handleResponse } = useApi();
    const { renderFormattedNumber } = useFormattedNumber();
    const { resource } = useWindowLocation();
    const { toMd, fromMd } = useMediaQueries();

    const [isLoading, setIsLoading] = useState(false);

    const handleCloseDialog = () => close(action);

    const isDeleteDeviceGroupsAction =
        action === GROUP_ACTIONS.DeleteDeviceGroups;

    const resourceCount = selectedRows?.length;
    const isSingle = resourceCount === 1 && !isDeleteDeviceGroupsAction;

    const getPayload = () => {
        return isSingle
            ? selectedRows[0]
            : {
                  source: TRI_SOURCES.Selected,
                  [TRI_SOURCES.Selected]: selectedRows
              };
    };

    const deselectAndClose = () => {
        deselectAllRows();
        handleCloseDialog();
    };

    const submitDeleteSelection = async () => {
        try {
            setIsLoading(true);

            const response = await (isSingle
                ? deleteData(resource, getPayload() as number)
                : bulkDeleteData(resource, getPayload()));

            if (isSingle) {
                deselectAndClose();
                handleResponse(response);
            } else {
                setBackgroundActionId(
                    response.data,
                    resource,
                    () => deselectAndClose(),
                    () => setIsLoading(false)
                );
            }
        } catch (error) {
            handleResponse(error);
            setIsLoading(false);
        }
    };

    const translatedResource = t(
        `Dialog##confirmations##delete resources##${resource}`
    );

    const description = t(
        "Dialog##confirmations##delete resource confirmation##first part",
        { resource: translatedResource }
    );

    const confirmationDescription = `
        ${description} 
        ${t(
            "Dialog##confirmations##delete resource confirmation##second part",
            {
                resource: translatedResource
            }
        )}`;

    const isBulkActionLoading = !isSingle && isLoading;

    return (
        <DialogWithConfirmation
            data-testid="delete-resource-for-selection-dialog"
            title={t(`Dialog##delete ${resource}`)}
            description={description}
            isActionsSeparator
            isTitleSeparator
            open={isOpen}
            count={resourceCount}
            source={TRI_SOURCES.Selected}
            confirmationDescription={confirmationDescription}
            submit={submitDeleteSelection}
            close={handleCloseDialog}
            isBulkActionLoading={isBulkActionLoading}
            css={css(fromMd && maxDialogWidth)}
            extendToMaxWidth={toMd}
            actions={
                <>
                    <Button
                        data-testid="close-delete-for-selection-button"
                        fullWidth
                        color={BUTTON_COLORS.Secondary}
                        size={BUTTON_SIZES.Normal}
                        variant={BUTTON_VARIANTS.TextOnly}
                        onClick={handleCloseDialog}
                        disabled={isLoading || isBulkActionLoading}
                    >
                        {t("Button##close")}
                    </Button>

                    <Button
                        data-testid="delete-resource-for-selection-button"
                        fullWidth
                        color={BUTTON_COLORS.Primary}
                        size={BUTTON_SIZES.Normal}
                        variant={BUTTON_VARIANTS.TextOnly}
                        type="submit"
                        isLoading={isLoading}
                        loaderText={
                            !isSingle ? t("Button##deleting") : undefined
                        }
                    >
                        {t("Button##delete")}
                    </Button>
                </>
            }
            slotProps={{
                transition: {
                    onExited: () => setIsLoading(false)
                }
            }}
        >
            <Tag
                data-testid="number-of-selected-items-for-selection"
                size={TAG_SIZES.Medium}
                color={TAG_COLORS.Primary}
                title={`${t(
                    `Dialog##selected ${resource}`
                )}: ${renderFormattedNumber(resourceCount)}`}
            />
        </DialogWithConfirmation>
    );
};

export default DeleteSelectedItems;
